import React, { useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  Tooltip,
  Popper
} from '@mui/material';
import { ConcepteIcon } from './IconMap';
import { flattenConceptTree, buildFullTreeWithEntities, resolveConcepteName } from '../../services/concepteGraphService';
import { useAppContext } from '../AppContext';

/**
 * Popper personalitzat per donar una amplada controlada i còmoda (500px)
 * que permet llegir totes les rutes i xips sense tallar textos i sense envair tota la pantalla.
 */
const CustomPopper = (props) => {
  return (
    <Popper
      {...props}
      style={{
        ...props.style,
        width: props.popperWidth || 500,
        maxWidth: '92vw',
        zIndex: 1400
      }}
      placement="bottom-start"
    />
  );
};

/**
 * Normalitza text eliminant accents, espais extres i convertint a majúscules.
 */
const normalizeText = (text) => {
  if (!text) return '';
  return String(text)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase();
};

/**
 * Component de selector jeràrquic de conceptes (Cascader / TreeSelect)
 * Suporta cerca, ruta completa (N1 > N2 > N3), agrupació i apartats de Vehicles i Persones.
 */
const SelectConcepteTree = ({
  label = 'Concepte',
  value, // Pot ser un string (nom del concepte o codi) o un objecte node
  onChange,
  tree = [],
  entitats = [],
  fullWidth = true,
  size = 'small',
  disabled = false,
  allowRootSelection = true, // Si es permet seleccionar també categories de nivell 1 o 2 (per resums)
  onlyLeaves = false, // Per a assignació de moviments (normalment es prioritzen fulles o nodes finals)
  placeholder = 'Selecciona concepte...',
  popperWidth = 500, // Amplada de 500px per veure els camins complets sense truncar
  sx = {}
}) => {
  // Construir arbre complet amb apartats de Vehicles i Membres al final de nivell 1
  const fullTree = useMemo(() => {
    return buildFullTreeWithEntities(tree, entitats);
  }, [tree, entitats]);

  // Aplanar arbre per a Autocomplete amb camí complet i filtres
  const options = useMemo(() => {
    const flat = flattenConceptTree(fullTree, [], false);
    // Filtrar inactius per a noves assignacions
    let filtered = flat.filter(item => item.actiu !== false);

    if (onlyLeaves) {
      filtered = filtered.filter(item => item.isLeaf);
    }

    return filtered;
  }, [fullTree, onlyLeaves]);

  const { conceptesList } = useAppContext();

  // Trobar l'opció seleccionada actual mitjançant el model canònic
  const selectedOption = useMemo(() => {
    if (!value) return null;

    if (typeof value === 'object' && value !== null) {
      // 1. PRIORITAT MÀXIMA: conceptePath exacte (identifica la branca inequívocament en un graf)
      if (value.conceptePath) {
        const valPathNorm = normalizeText(value.conceptePath);
        const matchPath = options.find(opt =>
          normalizeText(opt.conceptePath) === valPathNorm ||
          normalizeText(opt.pathNames?.join('/')) === valPathNorm ||
          normalizeText(opt.pathCodes?.join('/')) === valPathNorm
        );
        if (matchPath) return matchPath;
      }

      // 2. Coincidència per ID desambiguant per branca/relació
      if (value.id) {
        const matchesById = options.filter(opt => opt.id === value.id);
        if (matchesById.length === 1) return matchesById[0];
        if (matchesById.length > 1) {
          if (value.concepteClassic || value.concepte) {
            const classicUpper = normalizeText(value.concepteClassic || value.concepte);
            const matchClassic = matchesById.find(opt =>
              normalizeText(opt.concepteClassic) === classicUpper ||
              opt.pathCodes?.some(c => normalizeText(c) === classicUpper) ||
              opt.pathNames?.some(n => normalizeText(n) === classicUpper)
            );
            if (matchClassic) return matchClassic;
          }
          return matchesById[0];
        }
      }

      // 3. Coincidència per concepteClassic de compatibilitat backend
      if (value.concepteClassic || value.concepte) {
        const classicUpper = normalizeText(value.concepteClassic || value.concepte);
        const matchClassic = options.find(opt => normalizeText(opt.concepteClassic) === classicUpper);
        if (matchClassic) return matchClassic;
      }

      // 4. Coincidència per codi o nom
      const matchCodeOrName = options.find(opt => 
        (value.codi && opt.codi === value.codi) ||
        (value.nom && normalizeText(opt.nom) === normalizeText(value.nom))
      );
      if (matchCodeOrName) return matchCodeOrName;

      return null;
    }

    if (typeof value === 'number') {
      return options.find(opt => opt.id === value) || null;
    }

    if (typeof value === 'string') {
      const valTrimmed = value.trim();
      const valNorm = normalizeText(valTrimmed);

      // Si comença per 'node:'
      if (valTrimmed.startsWith('node:')) {
        const nodeIdNum = parseInt(valTrimmed.replace('node:', ''), 10);
        const matchesById = options.filter(opt => opt.id === nodeIdNum);
        if (matchesById.length >= 1) return matchesById[0];
      }

      // Si és un ID numèric en string
      if (/^\d+$/.test(valTrimmed)) {
        const numId = parseInt(valTrimmed, 10);
        const matchId = options.find(opt => opt.id === numId);
        if (matchId) return matchId;
      }

      // Si és un conceptePath o ruta jeràrquica (conté '/')
      if (valTrimmed.includes('/')) {
        // Coincidència directa per ruta completa de codis o noms
        const matchDirectPath = options.find(opt =>
          normalizeText(opt.conceptePath) === valNorm ||
          normalizeText(opt.pathNames?.join('/')) === valNorm ||
          normalizeText(opt.pathCodes?.join('/')) === valNorm ||
          normalizeText(opt.pathNames?.join(' / ')) === valNorm
        );
        if (matchDirectPath) return matchDirectPath;

        // Desglossar segments de la ruta canònica
        const segments = valTrimmed.split('/').map(s => s.trim()).filter(Boolean);
        if (segments.length >= 2) {
          const rootNorm = normalizeText(segments[0]);
          const leafNorm = normalizeText(segments[segments.length - 1]);

          // 1. Cercar opció canònica que coincideixi en arrel N1 i en la fulla exacta
          const matchBranch = options.find(opt => {
            const optRootName = normalizeText(opt.pathNames?.[0]);
            const optRootCode = normalizeText(opt.pathCodes?.[0]);
            const rootMatches = optRootName === rootNorm || optRootCode === rootNorm;
            const leafMatches = normalizeText(opt.nom) === leafNorm || normalizeText(opt.codi) === leafNorm;
            if (!rootMatches || !leafMatches) return false;

            if (segments.length > 2 && opt.pathNames?.[1]) {
              const seg2Norm = normalizeText(segments[1]);
              return normalizeText(opt.pathNames[1]) === seg2Norm || normalizeText(opt.pathCodes?.[1]) === seg2Norm;
            }
            return true;
          });
          if (matchBranch) return matchBranch;

          // 2. Fallback canònic per ancestres: si la fulla final (ex: "Indeterminat") no existeix com a fill a la branca,
          // fer fallback al subconcepte pare existent més proper (ex: "Assegurances > Vehicles" o "Assegurances")
          for (let len = segments.length - 1; len >= 1; len--) {
            const subSegments = segments.slice(0, len);
            const subRoot = normalizeText(subSegments[0]);
            const subLeaf = normalizeText(subSegments[subSegments.length - 1]);

            const matchAncestor = options.find(opt => {
              const optRootName = normalizeText(opt.pathNames?.[0]);
              const optRootCode = normalizeText(opt.pathCodes?.[0]);
              const rootMatches = optRootName === subRoot || optRootCode === subRoot;
              const leafMatches = normalizeText(opt.nom) === subLeaf || normalizeText(opt.codi) === subLeaf;
              return rootMatches && leafMatches;
            });

            if (matchAncestor) return matchAncestor;
          }
        }
      }

      // Si és un concepte clàssic legacy (ex: "SEGURO VEHICLE", "SEGURO LLAR", "ALIMENTACIO")
      const matchesClassic = options.filter(opt => normalizeText(opt.concepteClassic) === valNorm);
      if (matchesClassic.length === 1) {
        return matchesClassic[0];
      }
      if (matchesClassic.length > 1) {
        // En cas de múltiples opcions sota el mateix concepte clàssic, preferim el node categoria (pare intermedi)
        // si existeix per permetre assignacions directes a la categoria, o el primer resultat de la branca
        const categoryMatch = matchesClassic.find(opt => !opt.isLeaf);
        if (categoryMatch) return categoryMatch;
        return matchesClassic[0];
      }

      // Cerca directa per nom, codi o conceptePath
      const matchDirect = options.find(opt => 
        normalizeText(opt.nom) === valNorm || 
        normalizeText(opt.codi) === valNorm ||
        normalizeText(opt.conceptePath) === valNorm
      );
      if (matchDirect) return matchDirect;

      // Si conté '/', extreure segments per crear un dummy estructurat com a fallback segur
      if (valTrimmed.includes('/')) {
        const segments = valTrimmed.split('/').map(s => s.trim()).filter(Boolean);
        return {
          nom: segments[segments.length - 1],
          pathLabel: valTrimmed,
          pathNames: segments,
          isLeaf: true
        };
      }

      return { nom: value, pathLabel: value, isLeaf: true };
    }

    return null;
  }, [value, options]);

  const handleChange = (event, newValue) => {
    if (!onChange) return;

    if (!newValue) {
      onChange({ target: { name: 'concepte', value: '' } }, null);
      return;
    }

    // Obtenir el nom compatible que el backend reconeix:
    // S'utilitza el concepte clàssic de la branca seleccionada (o de l'arrel de la branca)
    const branchRootName = (newValue.pathNames && newValue.pathNames.length > 0) ? newValue.pathNames[0] : newValue.nom;
    const backendCompatibleName = newValue.concepteClassic || resolveConcepteName(branchRootName, conceptesList, fullTree) || branchRootName.toUpperCase();

    const syntheticEvent = {
      ...(event || {}),
      target: {
        name: 'concepte',
        value: backendCompatibleName || newValue.nom,
        displayName: newValue.nom,
        node: newValue
      }
    };

    onChange(syntheticEvent, newValue);
  };

  const getNodeIcon = (node) => {
    if (!node) return null;
    const parentConcept = (node.pathNames && node.pathNames.length > 1)
      ? node.pathNames[node.pathNames.length - 2]
      : (node.concepteClassic || (node.pathNames && node.pathNames[0]));
    return (
      <ConcepteIcon
        code={node.codi}
        concept={node.nom}
        fallback={[node.concepteClassic, parentConcept]}
        tipusNode={node.tipusNode}
        level={node.nivell || (node.pathNames ? node.pathNames.length : 1)}
        parentConcept={parentConcept}
        onlyIcon
        sx={{ fontSize: 18 }}
      />
    );
  };

  const handleOpen = () => {
    // Determinar el pare N1 legítim del concepte seleccionat a través de l'arbre
    const rootCategoryName =
      selectedOption?.pathNames?.[0] ||
      selectedOption?.nodeN1?.nom ||
      selectedOption?.agrupacio ||
      (typeof value === 'string' && value.includes('/') ? value.split('/')[0].trim() : null);

    const scrollToRootGroup = () => {
      const listbox = document.querySelector('.MuiAutocomplete-listbox');
      if (!listbox) return false;

      let targetGroupEl = null;

      if (rootCategoryName) {
        const rootNorm = normalizeText(rootCategoryName);

        // 1. Cercar directament pel nom canònic del grup (generat per groupBy amb opt.pathNames[0])
        const groups = Array.from(listbox.querySelectorAll('.MuiAutocomplete-group'));
        targetGroupEl = groups.find(groupEl => {
          const gName = groupEl.getAttribute('data-group-name') ||
            groupEl.querySelector('.MuiAutocomplete-groupLabel')?.textContent?.trim();
          return normalizeText(gName) === rootNorm;
        });

        // 2. Si no es troba per grup, cercar per etiquetes groupLabel
        if (!targetGroupEl) {
          const groupLabels = Array.from(listbox.querySelectorAll('.MuiAutocomplete-groupLabel, .MuiListSubheader-root'));
          const matchedLabel = groupLabels.find(lbl => normalizeText(lbl.textContent) === rootNorm);
          if (matchedLabel) {
            targetGroupEl = matchedLabel.closest('.MuiAutocomplete-group') || matchedLabel;
          }
        }
      }

      // Si no s'ha trobat el grup N1, fallback a l'element seleccionat
      if (!targetGroupEl && selectedOption) {
        const targetId = selectedOption.id;
        const targetPath = selectedOption.conceptePath;
        const targetNom = selectedOption.nom?.toUpperCase();
        const selectedEl =
          (targetId && listbox.querySelector(`[data-node-id="${targetId}"]`)) ||
          listbox.querySelector('[aria-selected="true"]') ||
          (targetPath && listbox.querySelector(`[data-node-path="${targetPath}"]`)) ||
          (targetNom && listbox.querySelector(`[data-node-name="${targetNom}"]`));
        if (selectedEl) {
          targetGroupEl = selectedEl.closest('.MuiAutocomplete-group') || selectedEl;
        }
      }

      if (targetGroupEl) {
        try {
          targetGroupEl.scrollIntoView({ block: 'start', behavior: 'auto' });
        } catch (e) {
          // fallback
        }
        const listboxRect = listbox.getBoundingClientRect();
        const elRect = targetGroupEl.getBoundingClientRect();
        const offsetDiff = elRect.top - listboxRect.top;
        if (Math.abs(offsetDiff) > 2) {
          listbox.scrollTop = Math.max(0, listbox.scrollTop + offsetDiff);
        }
        return true;
      }
      return false;
    };

    // Intent immediat i reforços progressius per posicionar la vista al pare N1
    requestAnimationFrame(() => scrollToRootGroup());
    setTimeout(() => scrollToRootGroup(), 50);
    setTimeout(() => scrollToRootGroup(), 150);
    setTimeout(() => scrollToRootGroup(), 300);
  };

  return (
    <>
      <Autocomplete
        options={options}
        value={selectedOption}
        onChange={handleChange}
        onOpen={handleOpen}
        autoHighlight={false}
        disableListWrap
        disabled={disabled}
        fullWidth={fullWidth}
        size={size}
        sx={{
          ...(fullWidth ? {} : { width: 'auto' }),
          ...sx
        }}
        PopperComponent={(props) => <CustomPopper {...props} popperWidth={popperWidth} />}
        slotProps={{
          popper: {
            sx: {
              width: `${popperWidth}px !important`,
              maxWidth: '92vw',
              zIndex: 1400,
              '& .MuiPaper-root': {
                width: `${popperWidth}px !important`,
                maxWidth: '92vw',
                boxShadow: 4,
                border: '1px solid #d0d7de',
                borderRadius: 1.5
              }
            },
            placement: 'bottom-start'
          }
        }}
        getOptionKey={(option) => {
          if (!option) return '';
          if (typeof option === 'string') return option;
          return option.conceptePath ? `opt-${option.id || ''}-${option.conceptePath}` : (option.id ? `node-${option.id}` : option.nom);
        }}
        getOptionLabel={(option) => {
          if (!option) return '';
          if (typeof option === 'string') return option;
          // Mostra un format compacte i clar per a columnes estretes: "Parking (Vacances)"
          if (option.pathNames && option.pathNames.length > 2) {
            const rootName = option.pathNames[0];
            const parentName = option.pathNames[option.pathNames.length - 2];
            return `${option.nom} (${rootName} > ${parentName})`;
          }
          if (option.pathNames && option.pathNames.length > 1) {
            const rootName = option.pathNames[0];
            return `${option.nom} (${rootName})`;
          }
          return option.nom || option.pathLabel || '';
        }}
        filterOptions={(allOpts, { inputValue }) => {
          const query = (inputValue || '').toLowerCase().trim();
          if (!query) return allOpts;
          const queryNorm = normalizeText(query);
          return allOpts.filter(opt => {
            const matchNom = normalizeText(opt.nom).includes(queryNorm);
            const matchPath = normalizeText(opt.pathLabel).includes(queryNorm);
            const matchSlashPath = opt.pathNames ? normalizeText(opt.pathNames.join('/')).includes(queryNorm) : false;
            const matchConceptePath = opt.conceptePath ? normalizeText(opt.conceptePath).includes(queryNorm) : false;
            const matchClassic = opt.concepteClassic ? normalizeText(opt.concepteClassic).includes(queryNorm) : false;
            const matchCodi = opt.codi ? normalizeText(opt.codi).includes(queryNorm) : false;
            return matchNom || matchPath || matchSlashPath || matchConceptePath || matchClassic || matchCodi;
          });
        }}
        isOptionEqualToValue={(option, val) => {
          if (!option || !val) return false;
          if (option === val) return true;
          if (option.conceptePath && val.conceptePath) {
            return normalizeText(option.conceptePath) === normalizeText(val.conceptePath);
          }
          if (option.id && val.id) {
            return option.id === val.id;
          }
          if (option.nom && val.nom) {
            return normalizeText(option.nom) === normalizeText(val.nom);
          }
          return false;
        }}
        groupBy={(option) => {
          if (option.pathNames && option.pathNames.length > 0) {
            return option.pathNames[0]; // Agrupar per Nivell 1
          }
          return option.agrupacio || 'Altres';
        }}
        renderGroup={(params) => (
          <li key={params.key} className="MuiAutocomplete-group" data-group-name={params.group}>
            <Box
              className="MuiAutocomplete-groupLabel MuiListSubheader-root"
              sx={{
                typography: 'caption',
                fontWeight: 'bold',
                color: 'primary.dark',
                bgcolor: '#f1f5f9',
                px: 1.5,
                py: 0.6,
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontSize: '0.78rem',
                letterSpacing: 0.5,
                textTransform: 'uppercase'
              }}
            >
              <ConcepteIcon concept={params.group} onlyIcon sx={{ fontSize: 18 }} />
              <span>{params.group}</span>
            </Box>
            <ul className="MuiAutocomplete-groupUl" style={{ padding: 0 }}>
              {params.children}
            </ul>
          </li>
        )}
        renderInput={(params) => (
          <Tooltip
            title={
              selectedOption
                ? `Ruta: ${selectedOption.pathLabel || selectedOption.nom}${selectedOption.concepteClassic ? ` (Clàssic: ${selectedOption.concepteClassic})` : ''} - Clic a la icona d'arbre per obrir el cercador emergent`
                : "Feu clic a la icona d'arbre per obrir l'arbre en finestra emergent (Popup)"
            }
            arrow
            placement="top"
          >
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              sx={{
                ...sx,
                '& .MuiInputBase-root': {
                  fontSize: '0.82rem',
                  pr: '4px !important'
                }
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    {selectedOption && (
                      <Box sx={{ display: 'inline-flex', mr: 0.5, alignItems: 'center' }}>
                        {getNodeIcon(selectedOption)}
                      </Box>
                    )}
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: params.InputProps.endAdornment
              }}
            />
          </Tooltip>
        )}
        renderOption={(props, option) => {
          const { key, ...otherProps } = props;
          const indent = (option.nivell ? (option.nivell - 1) : 0) * 10;
          const optionKey = option.conceptePath ? `opt-${option.id || ''}-${option.conceptePath}` : (key || (option.id ? `node-${option.id}` : option.nom));

          const showClassic = Boolean(
            option.concepteClassic &&
            option.nom &&
            option.concepteClassic.toUpperCase() !== option.nom.toUpperCase()
          );

          return (
            <Box
              key={optionKey}
              component="li"
              data-node-id={option.id}
              data-node-path={option.conceptePath}
              data-node-name={option.nom?.toUpperCase()}
              {...otherProps}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pl: `${indent + 8}px !important`,
                pr: '8px !important',
                py: '5px !important',
                borderBottom: '1px solid #f0f0f0',
                '&:hover': {
                  backgroundColor: '#f5f9ff !important'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexGrow: 1, mr: 1 }}>
                {getNodeIcon(option)}
                <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      fontWeight: option.nivell === 1 ? 'bold' : option.nivell === 2 ? 600 : 'normal',
                      color: option.nivell === 1 ? 'primary.main' : 'text.primary',
                      fontSize: '0.82rem'
                    }}
                  >
                    {option.nom}
                  </Typography>
                  {option.pathNames && option.pathNames.length > 1 && (
                    <Typography
                      variant="caption"
                      noWrap
                      title={option.pathLabel}
                      sx={{ color: 'text.secondary', display: 'block', fontSize: '0.68rem', lineHeight: 1.1 }}
                    >
                      {option.pathLabel}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 0.4, flexShrink: 0, alignItems: 'center' }}>
                {showClassic && (
                  <Tooltip title={`Mapejat clàssic legacy: ${option.concepteClassic}`}>
                    <Chip
                      label={option.concepteClassic}
                      size="small"
                      sx={{
                        fontSize: '0.58rem',
                        height: 16,
                        bgcolor: '#f1f3f4',
                        color: '#424242',
                        border: '1px solid #dcdcdc'
                      }}
                    />
                  </Tooltip>
                )}
                {option.tipusNode === 'VEHICLE' && (
                  <Chip label="Vehicle" size="small" sx={{ fontSize: '0.58rem', height: 16, backgroundColor: '#e1f5fe' }} />
                )}
                {option.tipusNode === 'PERSONA' && (
                  <Chip label="Membre" size="small" sx={{ fontSize: '0.58rem', height: 16, backgroundColor: '#f3e5f5' }} />
                )}
              </Box>
            </Box>
          );
        }}
      />
    </>
  );
};

export default SelectConcepteTree;
